import React, { forwardRef, useImperativeHandle, useState, useRef } from "react";
import { DocumentModal } from "~/components/DocumentModal";
import { MentionInput, Mention, type MentionInputRef } from "~/components/assistant/MentionInput";
import { SendButton } from "~/components/assistant/SendButton";
import SkillEditorModal, {
  type SkillFormData,
  type SkillFormErrors,
  type SkillTag,
} from "~/components/assistant/SkillEditorModal";
import { SkillSelector } from "~/components/assistant/SkillSelector";
import { SkillsPopup } from "~/components/assistant/SkillsPopup";
import { ToolSelector } from "~/components/assistant/ToolSelector";
import { useDrafts } from "~/components/assistant/hooks/useDrafts";
import type {
  MCPServer,
  PartialSkill,
  Skill,
  SkillCreate,
  SkillUpdate,
} from "~/components/assistant/types";
import { cn } from "~/lib/utils";

/** Max height for the chat input textarea in pixels. */
const TEXTAREA_MAX_HEIGHT = 120;

export interface ChatInputHandle {
  insertText: (text: string) => void;
}

interface ChatInputProps {
  contextId: string;
  currentConversationId: string | null;
  isSendingMessage: boolean;
  isCancelling: boolean;
  mcpServers: MCPServer[];
  selectedMcpServers: string[];
  onSendMessage: (message: string) => void;
  onCancelMessage: () => void;
  onToggleMcpServer: (serverId: string) => void;
  /** Placeholder text for the input when ready */
  placeholder: string;
  /** Whether to show the tool selector (defaults to true) */
  showToolSelector?: boolean;
  /** Whether skills are enabled for this user */
  skillsEnabled?: boolean;
  /** System skills from the API */
  systemSkills?: PartialSkill[];
  /** User skills from the API */
  userSkills?: PartialSkill[];
  /** Whether skills are currently loading */
  isLoadingSkills?: boolean;
  /** Callback to create a new skill */
  onCreateSkill?: (skill: SkillCreate) => Promise<void>;
  /** Callback to update an existing skill */
  onUpdateSkill?: (skillId: string, skill: SkillUpdate) => Promise<void>;
  /** Callback to delete a skill */
  onDeleteSkill?: (skillId: string) => Promise<void>;
  /** Callback to get a skill by ID (returns full skill with content) */
  onGetSkill?: (skillId: string) => Promise<Skill>;
}

/**
 * ChatInput manages its own input state to prevent re-renders of parent components.
 * This isolation is critical for performance - typing shouldn't cause the message list to re-render.
 */
export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(function ChatInput(
  {
    contextId,
    currentConversationId,
    isSendingMessage,
    isCancelling,
    mcpServers,
    selectedMcpServers,
    onSendMessage,
    onCancelMessage,
    onToggleMcpServer,
    placeholder,
    showToolSelector = true,
    skillsEnabled = false,
    systemSkills = [],
    userSkills = [],
    isLoadingSkills = false, // eslint-disable-line @typescript-eslint/no-unused-vars
    onCreateSkill,
    onUpdateSkill,
    onDeleteSkill,
    onGetSkill,
  },
  ref
) {
  const [isTextareaFocused, setIsTextareaFocused] = React.useState(false);
  const mentionInputRef = useRef<MentionInputRef>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [skillEditorOpen, setSkillEditorOpen] = useState(false);
  const [skillEditorErrors, setSkillEditorErrors] = useState<SkillFormErrors>({});
  const [savingSkill, setSavingSkill] = useState(false);
  const [editingSkillId, setEditingSkillId] = useState<string | null>(null);
  const [editingSkillData, setEditingSkillData] = useState<Partial<SkillFormData> | undefined>(
    undefined
  );
  const [loadingSkillContent, setLoadingSkillContent] = useState(false);
  const [helpModalOpen, setHelpModalOpen] = useState(false);

  const {
    draft: inputValue,
    setDraft: setInputValue,
    clearDraft,
  } = useDrafts({
    contextId,
    conversationId: currentConversationId,
  });

  const canSendMessage = !isSendingMessage && contextId && currentConversationId;

  useImperativeHandle(
    ref,
    () => ({
      insertText: (text: string) => {
        setInputValue(text);
        setTimeout(() => {
          if (skillsEnabled) {
            mentionInputRef.current?.focusAtEnd();
          } else {
            const textarea = textareaRef.current;
            if (textarea) {
              textarea.focus();
              textarea.setSelectionRange(text.length, text.length);
            }
          }
        }, 0);
      },
    }),
    [setInputValue, skillsEnabled]
  );

  const handleInputChange = (value: string) => {
    setInputValue(value);
  };

  // Handler for original textarea (when skills disabled)
  const handleTextareaInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = e.target;
    setInputValue(textarea.value);

    // Auto-resize textarea
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, TEXTAREA_MAX_HEIGHT)}px`;
  };

  const handleSendMessage = () => {
    if (!canSendMessage || inputValue.trim() === "") {
      return;
    }

    const outgoingContent = inputValue;
    clearDraft();

    onSendMessage(outgoingContent);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (isSendingMessage || !currentConversationId) {
        return;
      }
      handleSendMessage();
    }
  };

  const handleButtonClick = () => {
    if (isSendingMessage) {
      onCancelMessage();
    } else {
      handleSendMessage();
    }
  };

  const handleAddSkill = () => {
    setEditingSkillId(null);
    setEditingSkillData(undefined);
    setSkillEditorOpen(true);
    setSkillEditorErrors({});
  };

  const handleEditSkill = async (skillId: string) => {
    if (!onGetSkill) {
      return;
    }

    setEditingSkillId(skillId);
    setEditingSkillData(undefined);
    setSkillEditorOpen(true);
    setSkillEditorErrors({});
    setLoadingSkillContent(true);

    try {
      const skill = await onGetSkill(skillId);
      setEditingSkillData({
        name: skill.name,
        description: skill.description ?? "",
        tags: (skill.tags ?? []) as SkillTag[],
        content: skill.content,
      });
    } catch (error) {
      console.error("Failed to load skill:", error);
      setSkillEditorErrors({ general: "Failed to load skill content" });
    } finally {
      setLoadingSkillContent(false);
    }
  };

  const handleDeleteSkill = async (skillId: string) => {
    if (!onDeleteSkill) {
      return;
    }

    try {
      await onDeleteSkill(skillId);
    } catch (error) {
      console.error("Failed to delete skill:", error);
      // Could add toast notification here
    }
  };

  const handleSaveSkill = async (data: SkillFormData) => {
    if (!onCreateSkill || !onUpdateSkill) {
      return;
    }

    setSavingSkill(true);
    setSkillEditorErrors({});

    try {
      const payload = {
        name: data.name,
        description: data.description || undefined,
        content: data.content,
        tags: data.tags.length > 0 ? data.tags : undefined,
      };

      if (editingSkillId) {
        await onUpdateSkill(editingSkillId, payload);
      } else {
        await onCreateSkill({ ...payload, scope: "USER" });
      }

      setSkillEditorOpen(false);
      setEditingSkillId(null);
      setEditingSkillData(undefined);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to save skill";
      setSkillEditorErrors({ general: message });
    } finally {
      setSavingSkill(false);
    }
  };

  return (
    <div className="px-4">
      <div
        className={cn(
          "relative rounded-xl shrink-0 w-full max-w-[900px] mx-auto mb-4 shadow-[0_0_0_1px_rgba(84,72,49,0.08),0_3px_8px_rgba(0,0,0,0.04)] transition-all duration-200 hover:ring-1 hover:ring-gray-300",
          { "ring-1 ring-gray-300": isTextareaFocused }
        )}
      >
        <div className="flex flex-col justify-center relative size-full">
          <div className="flex flex-col gap-2 items-start justify-center pb-2 pl-4 pr-2 pt-3 relative w-full">
            <div className="flex flex-row items-start justify-between py-0.5 relative shrink-0 w-full">
              {skillsEnabled ? (
                <MentionInput
                  ref={mentionInputRef}
                  value={inputValue}
                  onChange={(value) => handleInputChange(value)}
                  onKeyDown={handleKeyPress}
                  onFocus={() => setIsTextareaFocused(true)}
                  onBlur={() => setIsTextareaFocused(false)}
                  placeholder={
                    !contextId
                      ? "Select a context to get started..."
                      : !currentConversationId
                      ? "Creating new conversation..."
                      : placeholder
                  }
                  maxHeight={TEXTAREA_MAX_HEIGHT}
                >
                  <Mention
                    trigger="/"
                    type="skill"
                    pattern={/\/[\w-]+/g}
                    className="text-block rounded px-1.5 py-0 inline-block mx-0.5 my-0.25"
                    data={[...systemSkills, ...userSkills]}
                    displayTransform={(skill: PartialSkill) => skill.name}
                    renderSuggestions={(props) => (
                      <div
                        style={{
                          position: "fixed",
                          top: props.position.top - 8,
                          left: props.position.left,
                          transform: "translateY(-100%)",
                          zIndex: 50,
                        }}
                        className="bg-white rounded-lg shadow-[0_0_0_1px_rgba(84,72,49,0.08),0_3px_8px_rgba(0,0,0,0.12)] overflow-hidden w-84"
                      >
                        <SkillsPopup
                          systemSkills={systemSkills}
                          userSkills={userSkills}
                          searchFilter={props.searchText}
                          onSelectSkill={(name) => props.onSelect(`/${name}`)}
                          onAddSkill={() => {
                            handleAddSkill();
                            props.onClose();
                          }}
                          onEditSkill={(id) => {
                            handleEditSkill(id);
                            props.onClose();
                          }}
                          onDeleteSkill={handleDeleteSkill}
                          onLearnMore={() => setHelpModalOpen(true)}
                          enableKeyboardNav={true}
                          onClose={props.onClose}
                        />
                      </div>
                    )}
                  />
                </MentionInput>
              ) : (
                <textarea
                  ref={textareaRef}
                  value={inputValue}
                  onChange={handleTextareaInput}
                  onKeyDown={handleKeyPress}
                  onFocus={() => setIsTextareaFocused(true)}
                  onBlur={() => setIsTextareaFocused(false)}
                  placeholder={
                    !contextId
                      ? "Select a context to get started..."
                      : !currentConversationId
                      ? "Creating new conversation..."
                      : placeholder
                  }
                  className={cn(
                    "flex-1 font-normal text-foreground text-sm text-left bg-transparent border-0 outline-none placeholder-gray-400 min-h-[20px] p-0 resize-none focus:outline-none shadow-none overflow-y-auto",
                    `max-h-[${TEXTAREA_MAX_HEIGHT}px]`
                  )}
                  aria-label="Chat message input"
                  rows={1}
                />
              )}
            </div>
            <div className="flex flex-row items-end justify-between py-0.5 relative shrink-0 w-full">
              <div className="flex flex-row items-center gap-0.5">
                {showToolSelector && (
                  <ToolSelector
                    servers={mcpServers}
                    selectedServerIds={selectedMcpServers}
                    onToggle={onToggleMcpServer}
                  />
                )}
                {skillsEnabled && (
                  <SkillSelector
                    systemSkills={systemSkills}
                    userSkills={userSkills}
                    onSelectSkill={(name) => {
                      // Unlike autocomplete insertion, toolbar button directly sets value, so need to manually add trailing space
                      setInputValue(inputValue + `/${name} `);
                      setTimeout(() => {
                        mentionInputRef.current?.focusAtEnd();
                      }, 0);
                    }}
                    onAddSkill={handleAddSkill}
                    onEditSkill={handleEditSkill}
                    onDeleteSkill={handleDeleteSkill}
                  />
                )}
              </div>

              <SendButton
                onClick={handleButtonClick}
                disabled={!inputValue.trim() || !currentConversationId}
                isLoading={isSendingMessage}
                isCancelling={isCancelling}
              />
            </div>
          </div>
        </div>
      </div>
      {skillsEnabled && (
        <>
          {skillEditorOpen && (
            <SkillEditorModal
              key={editingSkillId ?? "new"}
              open={skillEditorOpen}
              initialData={editingSkillData}
              onClose={() => {
                setSkillEditorOpen(false);
                setEditingSkillId(null);
                setEditingSkillData(undefined);
                setSkillEditorErrors({});
              }}
              onSubmit={handleSaveSkill}
              loading={savingSkill || loadingSkillContent}
              errors={skillEditorErrors}
              isEditing={!!editingSkillId}
            />
          )}
          <DocumentModal
            open={helpModalOpen}
            onClose={() => setHelpModalOpen(false)}
            title="AI Skills Guide"
            markdownPath="/assets/docs/agent/skills.md"
          />
        </>
      )}
      <div className="text-center text-xs text-gray-600 pb-2">
        AI can make mistakes. Review important details for accuracy.
      </div>
    </div>
  );
});
